// Stale hashed-chunk recovery for lazy routes and the entry stylesheet.
//
// A gateway update replaces `dist/control-ui` in place, so a document loaded before the
// update still references the old hashed chunk URLs; the first visit to a lazy
// route after the update 404s and the dynamic import rejects ("Importing a
// module script failed"). Reload the freshly served document in every browser,
// including WKWebView and plain-HTTP LAN origins without a service worker.
// Worker update announcements use this same guarded recovery; unsaved work
// blocks automatic reloads and leaves the Reload banner available.
import { raceWithTimeout, sleepWithAbort } from "@openclaw/retry";
import { CONTROL_UI_BUILD_INFO } from "../build-info.ts";
import { configuredUiDevGateway } from "../dev-gateway.ts";
import { t } from "../i18n/index.ts";
import { getSafeSessionStorage } from "../local-storage.ts";
import { resolveControlUiPaths } from "./browser.ts";
import { canReloadControlUiDocument } from "./document-reload-guard.ts";

const RELOAD_GUARD_STORAGE_KEY = "openclaw.controlUi.staleChunkReloadBuildId";
// Bounds document probes across rapid re-renders of the same error state.
const ATTEMPT_COOLDOWN_MS = 5_000;
// Keep timeout below the cooldown so a timed-out retry re-render cannot start
// another probe immediately while the gateway is still unreachable.
const DOCUMENT_PROBE_TIMEOUT_MS = 3_000;
const BUILD_RELOAD_JITTER_MS = 2_000;

// WebKit, Chromium, Firefox, and Vite's preload helper use these four phrases.
const MODULE_IMPORT_ERROR_PATTERN =
  /importing a module script failed|failed to fetch dynamically imported module|error loading dynamically imported module|unable to preload css/i;

type StaleChunkReloadDeps = {
  buildId?: string;
  storage?: Pick<Storage, "getItem" | "setItem"> | null;
  reload?: () => void;
  canReload?: () => boolean;
};

type RecoveryState = {
  buildId: string | null;
  attemptedAt: number;
  pending?: Promise<boolean>;
};

const recoveryByStorage = new WeakMap<object, RecoveryState>();
const unavailableStorage = {};
// A shared probe can release automatic and manual recovery in the same microtask.
// Admit one navigation before either path can replace the guard or reload.
let inFlightDocumentProbe: Promise<boolean> | null = null;

// These browser errors identify failed assets, not whether the deployed build changed.
export function isStaleChunkImportError(error: unknown): boolean {
  return error instanceof Error && MODULE_IMPORT_ERROR_PATTERN.test(error.message);
}

export function reloadControlUiDocument(url = new URL(window.location.href)): void {
  // The pre-app mount recovery strips this one-shot cache buster before bootstrap.
  url.searchParams.set("openclaw_mount_recovery", String(Date.now()));
  window.location.replace(url.href);
}

function probeControlUiDocument(): Promise<boolean> {
  if (inFlightDocumentProbe) {
    return inFlightDocumentProbe;
  }
  const probe = (async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), DOCUMENT_PROBE_TIMEOUT_MS);
    try {
      // Session links reject HEAD; probe the serving document without session query data.
      const url = new URL(window.location.href);
      const [routeBasePath, resourceBasePath] = resolveControlUiPaths(url.pathname);
      // Vite serves its own document while Gateway resources use the development proxy.
      const documentBasePath = configuredUiDevGateway() ? routeBasePath : resourceBasePath;
      url.pathname = `${documentBasePath}/index.html`;
      url.search = "";
      url.hash = "";
      const response = await fetch(url.href, {
        method: "HEAD",
        cache: "no-store",
        signal: controller.signal,
      });
      return response.ok;
    } catch {
      return false;
    } finally {
      clearTimeout(timeout);
    }
  })();
  const settledProbe = probe.finally(() => {
    if (inFlightDocumentProbe === settledProbe) {
      inFlightDocumentProbe = null;
    }
  });
  inFlightDocumentProbe = settledProbe;
  return settledProbe;
}

function persistGuardBuildId(
  storage: Pick<Storage, "getItem" | "setItem"> | null,
  buildId: string,
): boolean {
  if (!storage) {
    return false;
  }
  try {
    storage.setItem(RELOAD_GUARD_STORAGE_KEY, buildId);
    return true;
  } catch {
    return false;
  }
}

/**
 * Reload the document so stale hashed chunks resolve against the freshly
 * served index.html. Returns whether a reload was initiated. Reloads only when
 * the gateway answers a document probe — while it is restarting, a reload
 * would replace the whole document with a navigation error (fatal inside the
 * app webviews) instead of the recoverable panel error.
 */
export async function scheduleStaleChunkReload(deps: StaleChunkReloadDeps = {}): Promise<boolean> {
  if (deps.canReload?.() === false || !canReloadControlUiDocument()) {
    return false;
  }
  const storage = deps.storage === undefined ? getSafeSessionStorage() : deps.storage;
  const buildId = deps.buildId ?? CONTROL_UI_BUILD_INFO.buildId;
  // One automatic reload per build id: if the reloaded document still fails
  // with the same build, the build itself is broken and reloading cannot help.
  // A genuinely newer deployment ships a new build id and may recover again.
  try {
    if (storage?.getItem(RELOAD_GUARD_STORAGE_KEY) === buildId) {
      return false;
    }
  } catch {
    // Unreadable storage follows the same safe path as unavailable storage.
  }
  const now = Date.now();
  const storageIdentity = storage ?? unavailableStorage;
  let recovery = recoveryByStorage.get(storageIdentity);
  // A generic chunk failure cannot replace the server build already being
  // recovered. Only the Gateway owns a new target artifact and retry lifetime.
  if (
    deps.buildId === undefined &&
    recovery &&
    recovery.buildId !== buildId &&
    (recovery.buildId === null || recovery.pending)
  ) {
    return false;
  }
  if (
    recovery?.buildId === buildId &&
    !recovery.pending &&
    now - recovery.attemptedAt < ATTEMPT_COOLDOWN_MS
  ) {
    return false;
  }
  if (recovery?.buildId !== buildId || !recovery.pending) {
    const target: RecoveryState = { buildId, attemptedAt: now };
    recoveryByStorage.set(storageIdentity, target);
    const wait = () =>
      waitForReachableControlUiDocument(
        { timeoutMs: deps.buildId === undefined ? 0 : undefined },
        () =>
          recoveryByStorage.get(storageIdentity) === target &&
          target.buildId === buildId &&
          canReloadControlUiDocument(),
      );
    const delayMs =
      deps.buildId === undefined ? 0 : Math.floor(Math.random() * BUILD_RELOAD_JITTER_MS);
    target.pending = delayMs > 0 ? sleepWithAbort(delayMs).then(wait) : wait();
    recovery = target;
  }
  try {
    if (!(await recovery.pending)) {
      return false;
    }
  } finally {
    recovery.pending = undefined;
    recovery.attemptedAt = Date.now();
  }
  // A reload resets the in-memory state, so without a persisted guard a broken
  // build would reload forever. When storage is unavailable or rejects the
  // write, leave recovery to the manual Retry path instead of reloading.
  const reload = deps.reload ?? reloadControlUiDocument;
  if (
    !canReloadControlUiDocument() ||
    deps.canReload?.() === false ||
    recoveryByStorage.get(storageIdentity) !== recovery ||
    recovery.buildId !== buildId ||
    !persistGuardBuildId(storage, buildId)
  ) {
    return false;
  }
  recovery.buildId = null;
  reload();
  return true;
}

// A restarting gateway is the common case behind this banner: the stale chunk
// exists precisely because the gateway was just updated. Give the restart time
// to finish rather than declining the reload on the first failed probe.
const REACHABLE_WAIT_TIMEOUT_MS = 30_000;
const REACHABLE_WAIT_INTERVAL_MS = 1_000;

type ReachableReloadDeps = StaleChunkReloadDeps & {
  timeoutMs?: number;
};

async function waitForReachableControlUiDocument(
  deps: ReachableReloadDeps,
  isCurrent: () => boolean,
): Promise<boolean> {
  const deadline = Date.now() + (deps.timeoutMs ?? REACHABLE_WAIT_TIMEOUT_MS);
  for (let attempt = 0; ; attempt += 1) {
    if (!isCurrent()) {
      return false;
    }
    const remaining = deadline - Date.now();
    if (attempt > 0 && remaining <= 0) {
      return false;
    }
    // timeoutMs: 0 remains one bounded request.
    const reachable = await raceWithTimeout(
      probeControlUiDocument,
      remaining > 0 ? remaining : DOCUMENT_PROBE_TIMEOUT_MS,
      () => false,
    );
    if (!isCurrent()) {
      return false;
    }
    if (reachable) {
      return true;
    }
    const remainingWait = deadline - Date.now();
    if (remainingWait <= 0) {
      return false;
    }
    await sleepWithAbort(Math.min(REACHABLE_WAIT_INTERVAL_MS, remainingWait));
  }
}

/** User-initiated retry may bypass the automatic build guard, but never live ownership. */
export async function retryStaleChunkReloadWhenReachable(
  deps: ReachableReloadDeps = {},
): Promise<boolean> {
  if (
    !(await waitForReachableControlUiDocument(
      deps,
      () => deps.canReload?.() !== false && canReloadControlUiDocument(true),
    )) ||
    deps.canReload?.() === false ||
    !canReloadControlUiDocument(true)
  ) {
    return false;
  }
  const storage = deps.storage === undefined ? getSafeSessionStorage() : deps.storage;
  const storageIdentity = storage ?? unavailableStorage;
  const reload = deps.reload ?? reloadControlUiDocument;
  const recovery = recoveryByStorage.get(storageIdentity) ?? {
    buildId: CONTROL_UI_BUILD_INFO.buildId,
    attemptedAt: Date.now(),
  };
  recoveryByStorage.set(storageIdentity, recovery);
  const buildId = recovery.buildId;
  if (buildId === null) {
    return false;
  }
  recovery.buildId = null;
  persistGuardBuildId(storage, buildId);
  reload();
  return true;
}

/**
 * Vite dispatches `vite:preloadError` for every lazy-import rejection,
 * including ordinary module evaluation errors — reload only for recognized
 * stale-asset failures so a plain code bug cannot trigger a reload loop.
 */
export function installStaleChunkReloadListener(): () => void {
  const onPreloadError = (event: Event) => {
    const payload = (event as Event & { payload?: unknown }).payload;
    if (!isStaleChunkImportError(payload)) {
      return;
    }
    void scheduleStaleChunkReload();
  };
  window.addEventListener("vite:preloadError", onPreloadError);
  return () => window.removeEventListener("vite:preloadError", onPreloadError);
}

export function installMissingStylesheetRecovery(): () => void {
  let detected = false;
  let uninstalled = false;
  let banner: HTMLDivElement | null = null;

  const removeListeners = () => {
    window.removeEventListener("load", checkStylesheet);
    window.removeEventListener("error", onResourceError, true);
  };

  const showBanner = () => {
    if (uninstalled || banner) {
      return;
    }
    banner = document.createElement("div");
    banner.role = "alert";
    // All styles are inline because the entry stylesheet is broken by definition.
    banner.style.cssText =
      "position:fixed;inset:0 0 auto;z-index:2147483647;padding:12px;text-align:center;background:#1f2937;color:#fff;font:14px system-ui";
    const reloadButton = document.createElement("button");
    reloadButton.textContent = t("common.reload");
    reloadButton.addEventListener(
      "click",
      () => void retryStaleChunkReloadWhenReachable({ timeoutMs: 0 }),
    );
    banner.append(t("lazyView.stylesFailed"), " ", reloadButton);
    document.body.append(banner);
  };

  const detectMissingStylesheet = async () => {
    if (detected || uninstalled) {
      return;
    }
    detected = true;
    removeListeners();
    const reloaded = await scheduleStaleChunkReload();
    if (!reloaded) {
      showBanner();
    }
  };

  function checkStylesheet() {
    if (
      getComputedStyle(document.documentElement).getPropertyValue("--openclaw-css-ok").trim() ===
      "1"
    ) {
      removeListeners();
      return;
    }
    void detectMissingStylesheet();
  }

  function onResourceError(event: Event) {
    const resource = event.target;
    if (!(resource instanceof HTMLLinkElement) || !resource.relList.contains("stylesheet")) {
      return;
    }
    // Resource errors do not bubble, so capture is required. This can miss an
    // error fired before module evaluation; the load-time sentinel is authoritative.
    void detectMissingStylesheet();
  }

  window.addEventListener("error", onResourceError, true);
  if (document.readyState === "complete") {
    checkStylesheet();
  } else {
    window.addEventListener("load", checkStylesheet, { once: true });
  }

  return () => {
    uninstalled = true;
    removeListeners();
    banner?.remove();
    banner = null;
  };
}
